'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Package, ArrowLeft, Search } from 'lucide-react';
import Link from 'next/link';
import { PoweredByFooter } from '@/components/PoweredByFooter';
import { MarketIntelBanner } from '@/components/place/MarketIntelBanner';
import { recommendationsService } from '@/services/RecommendationsSingletonService';
import type { NationalCategoryRosterEntry } from '@/services/PlacesBrowsePublicService';
import { clientLogger } from '@/lib/client-logger';

interface Category {
  id: string;
  name: string;
  slug: string;
  googleCategoryId: string | null;
  icon?: string | null;
  storeCount: number;
  primaryStoreCount?: number;
  secondaryStoreCount?: number;
  productCount: number;
}




export default function AllCategoriesClient({
  roster,
}: {
  roster?: NationalCategoryRosterEntry[] | null;
}) {
  const router = useRouter();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch from materialized view for primary/secondary breakdown
        const result = await recommendationsService.getDirectoryMVCategories();

        if (result) {
          const transformedCategories = (result.categories || []).map((cat: any) => ({
            id: cat.id,
            name: cat.name,
            slug: cat.slug,
            googleCategoryId: cat.googleCategoryId,
            icon: cat.icon,
            storeCount: cat.storeCount,
            primaryStoreCount: cat.primaryStoreCount,
          secondaryStoreCount: cat.secondaryStoreCount,
            productCount: cat.totalProducts,
          }));
          setCategories(transformedCategories);
        }
      } catch (err) {
        clientLogger.error('Error fetching categories:', { detail: err });
        setError('Failed to load categories. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const filteredCategories = categories
    .filter((cat) => cat.storeCount > 0) // Only show categories that have stores
    .filter((cat) =>
      cat.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

  // National category packets keyed by slug + name — cards surface the
  // national framing excerpt when a packet exists for the category.
  const rosterByCategory = new Map<string, NationalCategoryRosterEntry>();
  for (const entry of roster ?? []) {
    if (entry.market.categoryKey) {
      rosterByCategory.set(entry.market.categoryKey.toLowerCase(), entry);
    }
    if (entry.market.categoryName) {
      rosterByCategory.set(entry.market.categoryName.toLowerCase(), entry);
    }
  }
  const rosterFor = (cat: Category) =>
    rosterByCategory.get(cat.slug.toLowerCase()) ??
    rosterByCategory.get(cat.name.toLowerCase());

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-neutral-600 dark:text-neutral-400">
            Loading categories...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
            Error Loading Categories
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">{error}</p>
          <Link
            href="/directory"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Directory
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Header */}
      <div className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Back Link */}
          <Link
            href="/directory"
            className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-blue-600 dark:hover:text-blue-400 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Directory
          </Link>

          {/* Title */}
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
            All Categories
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Browse stores by product category
          </p>

          {/* Search */}
          <div className="mt-6 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filteredCategories.length === 0 ? (
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-12 text-center">
            <Package className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
              {searchQuery ? 'No categories found' : 'No categories available'}
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400">
              {searchQuery
                ? `No categories match "${searchQuery}"`
                : 'Categories will appear here once stores add products.'}
            </p>
          </div>
        ) : (
          <>
          <div className="lg:flex lg:gap-8">
            <div className="min-w-0 flex-1">
            <div className="mb-4 text-sm text-neutral-600 dark:text-neutral-400">
              Showing {filteredCategories.length} of {categories.filter(cat => cat.storeCount > 0).length}{' '}
              {categories.filter(cat => cat.storeCount > 0).length === 1 ? 'category' : 'categories'} with stores
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() =>
                    router.push(`/directory/categories/${category.slug}`)
                  }
                  className="flex items-start gap-3 p-4 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-all bg-white dark:bg-neutral-800"
                >
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-2xl">
                    {category.icon || <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <h3 className="font-medium text-neutral-900 dark:text-white text-sm truncate">
                      {category.name}
                    </h3>
                    {(() => {
                      const entry = rosterFor(category);
                      const excerpt =
                        entry?.context?.category_overview ||
                        entry?.effective?.description;
                      return excerpt ? (
                        <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1 line-clamp-2">
                          {excerpt}
                        </p>
                      ) : null;
                    })()}
                    <div className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                      {/* Show primary/secondary breakdown if available */}
                      {category.primaryStoreCount !== undefined && category.secondaryStoreCount !== undefined ? (
                        <>
                          <span className="font-medium text-blue-600 dark:text-blue-400">
                            {category.primaryStoreCount} specialized
                          </span>
                          {category.secondaryStoreCount > 0 && (
                            <span className="text-neutral-500">
                              {' + '}{category.secondaryStoreCount} also carry
                            </span>
                          )}
                          <div className="mt-0.5">
                            {category.productCount}{' '}
                            {category.productCount === 1 ? 'product' : 'products'}
                          </div>
                        </>
                      ) : (
                        <>
                          {category.storeCount}{' '}
                          {category.storeCount === 1 ? 'store' : 'stores'}
                          {' · '}
                          {category.productCount}{' '}
                          {category.productCount === 1 ? 'product' : 'products'}
                        </>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            </div>

            {/* Tall banner slot (300x600) — right rail beside the category
                grid; sticky on desktop, stacks below on mobile. No teaser on
                this aggregate surface → house creative fallback. */}
            <aside className="mt-8 shrink-0 lg:mt-0 lg:w-[336px]">
              <div className="lg:sticky lg:top-6">
                <MarketIntelBanner variant="tall" surfaceType="directory" />
              </div>
            </aside>
          </div>

          {/* Square banner slot (300x250) — in-flow, end of the grid. */}
          <div className="mt-10 flex justify-center">
            <MarketIntelBanner variant="square" surfaceType="directory" />
          </div>
          </>
        )}
      </div> 
    </div>
  );
}
